import React, { useState } from 'react';
import { PinIcon, BuildingIcon, HashIcon, MapPinIcon } from 'lucide-react';
import { Loan } from '../types/loan.types';
import { ProgressRing } from './ProgressRing';
import { DocumentsTab } from './DocumentsTab';
import { LoanInfoTab } from './LoanInfoTab';
import { ActivityTab } from './ActivityTab';
interface LoanDetailPanelProps {
  loan: Loan;
  isPinned: boolean;
  onTogglePin: () => void;
  onDocumentUpload: (documentId: string, file: File, fileName: string) => void;
}
type TabType = 'documents' | 'info' | 'activity';
export function LoanDetailPanel({
  loan,
  isPinned,
  onTogglePin,
  onDocumentUpload
}: LoanDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('documents');
  const completedDocs = loan.requiredDocuments.filter(doc => doc.status !== 'missing').length;
  const totalDocs = loan.requiredDocuments.length;
  const completionPercentage = Math.round(completedDocs / totalDocs * 100);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const tabs: {
    id: TabType;
    label: string;
  }[] = [{
    id: 'documents',
    label: 'Documents'
  }, {
    id: 'info',
    label: 'Loan Info'
  }, {
    id: 'activity',
    label: 'Activity'
  }];
  return <div className="bg-white rounded-xl shadow-lg border border-gray-200 h-full flex flex-col" data-id="element-86">
      {/* Header */}
      <div className="p-6 border-b border-gray-200" data-id="element-87">
        <div className="flex items-start justify-between gap-4" data-id="element-88">
          <div className="flex-1 min-w-0" data-id="element-89">
            <h2 className="text-2xl font-bold text-gray-900 truncate" data-id="element-90">
              {loan.borrowerName}
            </h2>
            <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-91">
              {formatCurrency(loan.loanAmount)}
            </p>
          </div>
          <div className="flex items-start gap-4" data-id="element-92">
            <ProgressRing percentage={completionPercentage} size={80} strokeWidth={6} data-id="element-93" />
            <button onClick={onTogglePin} className={`
                p-2 rounded-lg transition-all
                ${isPinned ? 'text-amber-600 bg-amber-100 hover:bg-amber-200' : 'text-gray-400 bg-gray-100 hover:bg-gray-200 hover:text-gray-600'}
              `} title={isPinned ? 'Unpin loan' : 'Pin loan'} aria-label={isPinned ? 'Unpin loan' : 'Pin loan'} data-id="element-94">
              <PinIcon className="w-5 h-5" fill={isPinned ? 'currentColor' : 'none'} data-id="element-95" />
            </button>
          </div>
        </div>

        {/* Loan Details */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-96">
          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-97">
            <BuildingIcon className="w-4 h-4 text-gray-400" aria-hidden="true" data-id="element-98" />
            <span className="truncate" data-id="element-99">{loan.brokerageName}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-100">
            <HashIcon className="w-4 h-4 text-gray-400" aria-hidden="true" data-id="element-101" />
            <span className="font-mono" data-id="element-102">{loan.loanNumber}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-103">
            <MapPinIcon className="w-4 h-4 text-gray-400" aria-hidden="true" data-id="element-104" />
            <span className="truncate" data-id="element-105">{loan.propertyAddress}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-106">
        <nav className="flex -mb-px px-6" aria-label="Tabs" data-id="element-107">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                py-4 px-6 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `} aria-current={activeTab === tab.id ? 'page' : undefined} data-id="element-108">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-109">
        {activeTab === 'documents' && <DocumentsTab loan={loan} onDocumentUpload={onDocumentUpload} data-id="element-110" />}
        {activeTab === 'info' && <LoanInfoTab loan={loan} data-id="element-111" />}
        {activeTab === 'activity' && <ActivityTab loan={loan} data-id="element-112" />}
      </div>
    </div>;
}